import React, { useCallback, useEffect, useMemo } from "react";
import { Alert, Box, Button, Skeleton, Stack } from "@mui/material";

import { fetchClientReportingScopeAction } from "actions/client-reporting";
import { useActionHook } from "hooks";
import { PortfolioAnalysis } from "pages/Portfolio-Analysis/PortfolioAnalysis";
import { IApiServiceError } from "store";
import {
  ClientReportingScopeError,
  ClientReportingScopeResponse,
  FetchClientReportingScopeAction
} from "store/types";

interface ClientReportingTabProps {
  clientId: string;
}

const STATUS_MESSAGE_MAP: Record<number, string> = {
  403: "You do not have permission to view reporting for this client.",
  404: "Client not found.",
  500: "Unable to load reporting data. Please try again later."
};

const TIMEOUT_MESSAGE = "Request timed out. Please try again.";
const DEFAULT_ERROR_MESSAGE = "Unable to load reporting data. Please try again later.";

export const ClientReportingTab: React.FC<ClientReportingTabProps> = ({ clientId }) => {
  // 1. Original action instantiation to prevent "Invalid Hook Call"
  const scopedAction = fetchClientReportingScopeAction();
  const actionSource: FetchClientReportingScopeAction =
    (typeof scopedAction === "function" ? scopedAction : undefined) ??
    (fetchClientReportingScopeAction as unknown as FetchClientReportingScopeAction);

  const {
    loading,
    data,
    error,
    action,
    resetError: resetActionError
  } = useActionHook(actionSource, {
    defaultLoadingState: true
  });

  const resetError = resetActionError ?? (() => undefined);

  useEffect(() => {
    if (!clientId) {
      return;
    }

    resetError();
    void action(clientId);

    // 2. CRITICAL FIX: Break the infinite re-render loop!
    // We intentionally omit 'action' and 'resetError' from the dependency array 
    // to ensure the fetch only happens once per client ID.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const normalizedError = useMemo(() => normalizeClientReportingError(error), [error]);

  const handleRetry = useCallback(() => {
    resetError();
    if (clientId) {
      void action(clientId);
    }
  }, [action, clientId, resetError]);

  // 3. SAFE UNWRAPPING: Protect against varying API response structures
  const investorIds = useMemo<number[]>(() => {
    if (!data) return [];
    // Fallback safely whether data is { data: [...] } or just [...]
    const unwrapped = (data as any).data || data;
    return Array.isArray(unwrapped) ? unwrapped : [];
  }, [data]);

  if (loading) {
    return (
      <Stack data-testid="client-reporting-loading" spacing={2} sx={{ width: "100%" }}>
        <Skeleton variant="rectangular" height={48} />
        <Skeleton variant="rectangular" height={320} />
      </Stack>
    );
  }

  if (normalizedError) {
    const message = normalizedError.timeout
      ? TIMEOUT_MESSAGE
      : STATUS_MESSAGE_MAP[normalizedError.status ?? 0] ?? normalizedError.message ?? DEFAULT_ERROR_MESSAGE;

    return (
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Alert severity="error">{message}</Alert>
        <Box>
          <Button variant="contained" color="primary" onClick={handleRetry}>
            Retry
          </Button>
        </Box>
      </Stack>
    );
  }

  if (investorIds.length === 0) {
    return (
      <Alert severity="info">
        This client has no linked entities. Add entity allocations to enable reporting.
      </Alert>
    );
  }

  const isSingleInvestor = investorIds.length === 1;

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {isSingleInvestor && (
        <Alert severity="info">Reporting scoped to the single linked investor.</Alert>
      )}
      <PortfolioAnalysis scopedInvestorIds={investorIds} />
    </Stack>
  );
};

function normalizeClientReportingError(error: unknown): ClientReportingScopeError | null {
  if (!error) {
    return null;
  }

  if (typeof error === "object" && error) {
    const candidate = error as Partial<ClientReportingScopeError> & Partial<IApiServiceError> & { code?: string; aborted?: boolean };

    if (candidate.aborted) {
      return null;
    }

    const status = candidate.status ?? candidate.response?.status;
    const timeout = Boolean(candidate.timeout || status === 408 || candidate.code === "ECONNABORTED");
    const message =
      (typeof candidate.message === "string" && candidate.message.length > 0
        ? candidate.message
        : undefined) ?? DEFAULT_ERROR_MESSAGE;

    return {
      status,
      timeout,
      message
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message || DEFAULT_ERROR_MESSAGE
    };
  }

  return {
    message: DEFAULT_ERROR_MESSAGE
  };
}

export type { ClientReportingScopeResponse };